import { Menu, Modal, notification } from "antd";
import { Link } from "react-router-dom";
import React from "react";

import OrderService from "../../common/interfaces/OrderService";
import { differenceInHours } from "date-fns";
import { axiosInstance, axiosInstanceWork } from "../../services/axios";
import { useStatement } from "../../contexts/statement";
import { RescheduleModal, useRescheduleModal } from "./RescheduleModal";
import { isTomorrow } from "../../utils/date";
import { shouldAllowReschedule } from "../../services/http/order-service";

interface Props {
  status: string;
  id: string;
  data: OrderService | undefined;
  refetch: () => any;
}

export default function StatusMenu({ status, id, data, refetch }: Props) {
  const { setStatement } = useStatement();
  const handleUpdateStatement = () => setStatement(true);

  const rescheduleModal = useRescheduleModal();

  const [triggeredReport, setTriggeredReport] = React.useState(false);

  if (!data) return null;

  const contestation = data?.inspection?.contestation;

  const dateToday = new Date();
  const orderServiceDateInspectionAfter7Days = new Date(data.date_inspection);
  orderServiceDateInspectionAfter7Days.setDate(
    orderServiceDateInspectionAfter7Days.getDate() + 7
  );
  orderServiceDateInspectionAfter7Days.setHours(24);
  orderServiceDateInspectionAfter7Days.setMinutes(0);
  orderServiceDateInspectionAfter7Days.setHours(0);

  function generateReport(
    orderService: OrderService,
    withoutPhoto: boolean = false
  ): void {
    setTriggeredReport(true);
    notification.info({
      key: "report_info",
      message:
        "O seu relatório está sendo gerado, estará disponível em alguns instantes.",
    });

    axiosInstance
      .post(
        `/order-service/${orderService.id}/generate-report-order-service`,
        {
          withoutPhoto,
        }
      )
      .then(() => {
        notification.close("report_info");
        notification.success({
          message: `Geração do relatório foi enviado com sucesso. Aguarde alguns segundos e atualize a página.`,
        });
      })
      .catch(() => {
        notification.close("report_info");
        notification.error({
          message:
            "Houve um erro ao gerar o relatório. Tente novamente mais tarde.",
        });
      });
  }

  function generateZipOrderService(
    orderService: OrderService
  ): void {
    setTriggeredReport(true);
    notification.info({
      key: "zip_info",
      message:
        "O seu arquivo com as mídias da vistoria está sendo gerado, estará disponível em alguns instantes.",
    });

    axiosInstanceWork
      .post(
        `/order-service/${orderService.id}/generate-zip-order-service-inspection`
      )
      .then(() => {
        notification.close("zip_info");
        notification.success({
          message: `Sua solicitação foi enviada com sucesso. Aguarde alguns segundos e atualize a página.`,
        });
      })
      .catch(() => {
        notification.close("zip_info");
        notification.error({
          message:
            "Houve um erro ao gerar o arquivo de mídias. Tente novamente mais tarde.",
        });
      });
  }

  const handleCancel = () => {
    const { date_solicitation, date_inspection } = data;

    // const diffInHoursDateNowToDateInspection = differenceInHours(Date.now(), new Date(date_inspection))

    let message = {
      title: "Cancelar pedido",
      content: "Tem certeza que deseja cancelar?",
      okCancel: true,
      cancelText: "Voltar",
      okText: "Confirmar",
      onOk: () => {
        axiosInstance
          .post(`/client/order-service/${id}/cancel`)
          .then(() => {
            notification.success({
              message: "O pedido foi cancelado com sucesso!",
            });
            handleUpdateStatement();
            refetch();
          });
      },
    };

    if (isTomorrow(new Date(date_inspection))) {
      if (differenceInHours(Date.now(), new Date(date_solicitation)) > 2) {
        Modal.warn({
          ...message,
          content:
            "A solicitação foi realizada há mais de uma 2h, o cancelamento acarretará em consumo.",
          onOk: () => {
            axiosInstance
              .post(`/client/order-service/${id}/cancel`)
              .then(() => {
                refetch();
                handleUpdateStatement();
                notification.success({
                  message: "O pedido foi cancelado com sucesso!",
                });
              });
          },
        });
        return;
      }
    }

    Modal.warn(message);
  };

  return (
    <Menu>
      <Menu.Item key="details">
        <Link to={`/my-requests/${id}`}>Detalhes do pedido</Link>
      </Menu.Item>
      {orderServiceDateInspectionAfter7Days >= dateToday && (
        <>
          <Menu.Item key="edit">
            <Link to={`/my-requests/${id}/edit`}>Editar pedido</Link>
          </Menu.Item>
          <Menu.Item key="attach">
            <Link to={`/my-requests/${id}/edit?tab=attachments`}>
              Anexar ao pedido
            </Link>
          </Menu.Item>
        </>
      )}
      {(status === "Finalizado" ||
        status === "Contestado" ||
        status === "Respondido") && (
        <>
          <Menu.Item
            disabled={
              !data?.inspection?.pathReportOrderService || triggeredReport
            }
          >
            <a
              href={data?.inspection?.pathReportOrderService}
              target="_blank"
              rel="noreferrer"
            >
              Visualizar relatório
            </a>
          </Menu.Item>
          <Menu.Item
            disabled={!data?.inspection?.pathReportOrderServiceWithoutPhoto}
          >
            <a
              href={data?.inspection?.pathReportOrderServiceWithoutPhoto}
              target="_blank"
              rel="noreferrer"
            >
              Visualizar relatório sem foto
            </a>
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              const reportWithoutPhoto = true;
              generateReport(data, reportWithoutPhoto);
            }}
          >
            Gerar relatório sem foto
          </Menu.Item>
        </>
      )}
      {(status === "Finalizado" ||
        status === "Contestado" ||
        status === "Respondido") && (
        <>
          {data?.inspection?.pathZip && 
            <Menu.Item disabled={!data?.inspection?.pathZip}>
              <a
                href={data?.inspection?.pathZip}
                target="_blank"
                rel="noreferrer"
              >
                Baixar mídias da vistoria
              </a>
            </Menu.Item>
          }
          { data?.inspection?.createdAt && new Date(data?.inspection?.createdAt) >= new Date('2024-03-01') &&
            <Menu.Item
              onClick={() => {
                generateZipOrderService(data);
              }}
            >
              Solicitar mídias da vistoria
            </Menu.Item>
          }
        </>
      )}
      {status === "Agendado" && (
        <>
          <Menu.Item key="cancel" onClick={handleCancel}>
            Cancelar pedido
          </Menu.Item>
          {shouldAllowReschedule(data) && !data.inspection?.dateFinish && (
            <Menu.Item key="schedule" onClick={rescheduleModal.open}>
              Reagendar pedido
            </Menu.Item>
          )}
          <RescheduleModal
            data={data}
            visible={rescheduleModal.isOpen}
            onClose={rescheduleModal.close}
            onSave={refetch}
          />
        </>
      )}
      {status === "Entregue" && <Menu.Item key="contest">Contestar</Menu.Item>}
      {(status === "Contestado" ||
        status === "Respondido" ||
        data?.inspection?.pathReportContestation) && (
        <>
          <Menu.Item key="view3">
            <a
              href={data?.inspection?.pathReportContestation}
              target="_blank"
              rel="noreferrer"
            >
              Visualizar contestação
            </a>
          </Menu.Item>
        </>
      )}
      {(status === "Contestado" ||
        status === "Respondido" ||
        status === "Finalizado") && (
        <>
          <Menu.Item key="view">
            <a
              href={`https://contestacao.bhousesi.com.br/orderservice/${data?.id}/contestation`}
              target="_blank"
              rel="noreferrer"
            >
              Acessar contestação
            </a>
          </Menu.Item>
          <Menu.Item key="view1">
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  `https://contestacao.bhousesi.com.br/orderservice/${data?.id}/contestation`
                );
                notification.success({
                  message: "Link copiado com sucesso!",
                });
              }}
            >
              Copiar link da contestação
            </button>
          </Menu.Item>
        </>
      )}
    </Menu>
  );
}
